/**
 * RFC 4180-compliant CSV utilities shared by BulkSend, BulkPreview, and
 * CsvValidator. Handles quoted fields containing commas/newlines and
 * Windows CRLF line endings — both silently corrupted data in v1.
 */

/** Tokenize one logical CSV record into fields. */
const parseLine = (line) => {
    const fields = []
    let field = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    field += '"'
                    i++
                } else {
                    inQuotes = false
                }
            } else {
                field += ch
            }
        } else if (ch === '"') {
            inQuotes = true
        } else if (ch === ',') {
            fields.push(field)
            field = ''
        } else {
            field += ch
        }
    }
    fields.push(field)
    return fields.map((f) => f.trim())
}

/**
 * Parse CSV text into { headers, rows } where rows is an array of objects
 * keyed by header. Returns { headers: [], rows: [] } for empty input.
 */
export const parseCSV = (text) => {
    if (!text || !text.trim()) return { headers: [], rows: [] }
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim()

    // Split into logical records, respecting quoted newlines
    const records = []
    let current = ''
    let inQuotes = false
    for (const ch of normalized) {
        if (ch === '"') inQuotes = !inQuotes
        if (ch === '\n' && !inQuotes) {
            records.push(current)
            current = ''
        } else {
            current += ch
        }
    }
    if (current) records.push(current)

    const nonEmpty = records.filter((r) => r.trim().length > 0)
    if (nonEmpty.length === 0) return { headers: [], rows: [] }

    const headers = parseLine(nonEmpty[0])
    const rows = nonEmpty.slice(1).map((record) => {
        const values = parseLine(record)
        return Object.fromEntries(headers.map((h, i) => [h, values[i] ?? '']))
    })
    return { headers, rows }
}

/** Quote a value for CSV output when it contains a delimiter/quote/newline. */
export const escapeCSVField = (value) => {
    const str = String(value ?? '')
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}

/** Serialize an array of objects back to CSV text. */
export const toCSV = (rows, headers) => {
    if (!rows?.length) return ''
    const cols = headers ?? Object.keys(rows[0])
    const lines = [cols.map(escapeCSVField).join(',')]
    for (const row of rows) {
        lines.push(cols.map((c) => escapeCSVField(row[c])).join(','))
    }
    return lines.join('\n')
}
